import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Assistant, AssistantStatus } from '@/application/assistants/assistantsTypes';
import type { Role } from '@/application/permissions/permissionsTypes';
import {
  assignAssistantClinics,
  assignAssistantRole,
  deleteAssistant,
  listAssistants,
  recoverAssistantPassword,
  renameAssistant,
  resetAssistantTwoFactor,
  setAssistantStatus,
} from '@/infrastructure/http/assistantsApi';
import { listRoles } from '@/infrastructure/http/permissionsApi';
import {
  AssistantActions,
  AssistantStatusBadge,
  assistantRoleName,
} from '@/presentation/components/assistants/AssistantActions';
import { AssistantCard } from '@/presentation/components/assistants/AssistantCard';
import { CreateAssistantWizard } from '@/presentation/components/assistants/CreateAssistantWizard';
import { EditAssistantModal } from '@/presentation/components/assistants/EditAssistantModal';
import { actionClass } from '@/presentation/components/common/actionButtons';
import { EmptyState } from '@/presentation/components/common/EmptyState';
import { ModalPortal } from '@/presentation/components/common/ModalPortal';
import { ResponsiveDataList, type ResponsiveColumnDef } from '@/presentation/components/common/ResponsiveDataList';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';

export function AssistantsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isOwner, isSimulating } = usePermissions();
  const canManage = isOwner && !isSimulating;
  const canManageRoles = canManage;
  const goToRoles = () => navigate('/team/roles');
  const ownerClinics = user?.scope.allowedTenantIds ?? [];
  const clinicNames = useClinicNames();
  const nameFor = (clinicId: string) => clinicNames[clinicId] ?? clinicId;

  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<Assistant | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Assistant | null>(null);
  const [pendingRecover, setPendingRecover] = useState<Assistant | null>(null);
  const [pendingResetTwoFactor, setPendingResetTwoFactor] = useState<Assistant | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const errorText = useCallback(
    (code: string) =>
      t(`assistants.errors.${code}`, {
        defaultValue: t(`permissions.errors.${code}`, { defaultValue: t('assistants.errors.generic') }),
      }),
    [t]
  );

  const refresh = useCallback(async () => {
    if (!token || !canManage) {
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [assistantsResult, rolesResult] = await Promise.allSettled([
        listAssistants(token),
        listRoles(token),
      ]);
      if (assistantsResult.status === 'fulfilled') {
        setAssistants(assistantsResult.value);
      } else {
        setLoadError(errorText(assistantsResult.reason instanceof Error ? assistantsResult.reason.message : 'generic'));
      }
      if (rolesResult.status === 'fulfilled') {
        setRoles(rolesResult.value);
      }
    } finally {
      setLoading(false);
    }
  }, [token, canManage, errorText]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleAssignRole = async (id: string, panelRoleId: string | null) => {
    if (!token) {
      return;
    }
    setActionError(null);
    try {
      upsert(await assignAssistantRole(token, id, panelRoleId));
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
      throw err;
    }
  };

  /** Reemplaza en la lista local el asistente devuelto por una acción. */
  const upsert = useCallback((updated: Assistant) => {
    setAssistants((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  }, []);

  const openWizard = () => {
    setActionError(null);
    setWizardOpen(true);
  };

  const handleRename = async (id: string, name: string) => {
    if (!token) {
      return;
    }
    setActionError(null);
    try {
      upsert(await renameAssistant(token, id, name));
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
      throw err;
    }
  };

  const handleToggleStatus = async (assistant: Assistant) => {
    if (!token) {
      return;
    }
    setActionError(null);
    const next: AssistantStatus = assistant.status === 'active' ? 'disabled' : 'active';
    try {
      upsert(await setAssistantStatus(token, assistant.id, next));
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
      throw err;
    }
  };

  const handleSaveClinics = async (id: string, clinicaIds: string[]) => {
    if (!token) {
      return;
    }
    setActionError(null);
    try {
      upsert(await assignAssistantClinics(token, id, clinicaIds));
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
      throw err;
    }
  };

  const handleRecoverPassword = async () => {
    if (!token || !pendingRecover) {
      return;
    }
    setActionError(null);
    setNotice(null);
    try {
      await recoverAssistantPassword(token, pendingRecover.id);
      setNotice(t('assistants.recover.sent', { email: pendingRecover.email }));
      setPendingRecover(null);
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
    }
  };

  const handleResetTwoFactor = async () => {
    if (!token || !pendingResetTwoFactor) {
      return;
    }
    setActionError(null);
    setNotice(null);
    try {
      await resetAssistantTwoFactor(token, pendingResetTwoFactor.id);
      setNotice(t('assistants.resetTwoFactor.done', { email: pendingResetTwoFactor.email }));
      setPendingResetTwoFactor(null);
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
    }
  };

  const handleDelete = async () => {
    if (!token || !pendingDelete) {
      return;
    }
    setActionError(null);
    try {
      await deleteAssistant(token, pendingDelete.id);
      setAssistants((current) => current.filter((item) => item.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      setActionError(errorText(err instanceof Error ? err.message : 'generic'));
    }
  };

  if (!canManage) {
    return (
      <div className={embedded ? 'space-y-2' : 'space-y-2 p-4 xl:p-6'}>
        <h1 className="text-2xl font-bold text-gray-950 dark:text-white">{t('assistants.forbidden.title')}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('assistants.forbidden.message')}</p>
      </div>
    );
  }

  const createButton = (
    <button
      type="button"
      data-testid="assistants-open-create"
      onClick={openWizard}
      className={`${actionClass('primary', 'page')} w-full sm:w-auto`}
    >
      {t('assistants.wizard.openCreate')}
    </button>
  );

  const columns: ResponsiveColumnDef<Assistant>[] = [
    {
      key: 'person',
      header: t('assistants.table.name'),
      width: '28%',
      cellClassName: 'min-w-0',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900 dark:text-gray-100" title={row.nombre}>
            {row.nombre}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={row.email}>
            {row.email}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('assistants.table.status'),
      width: '12%',
      cellClassName: 'whitespace-nowrap',
      cell: (row) => <AssistantStatusBadge status={row.status} />,
    },
    {
      key: 'role',
      header: t('assistants.table.role'),
      width: '16%',
      cellClassName: 'min-w-0',
      cell: (row) => (
        <span className="block truncate" title={assistantRoleName(row, roles, t('assistants.role.none'))}>
          {assistantRoleName(row, roles, t('assistants.role.none'))}
        </span>
      ),
    },
    {
      key: 'clinics',
      header: t('assistants.table.clinics'),
      width: '12%',
      cellClassName: 'whitespace-nowrap',
      cell: (row) => t('assistants.clinics.count', { count: row.clinicaIds.length }),
    },
    {
      key: 'actions',
      header: t('assistants.table.actions'),
      width: '32%',
      cell: (row) => (
        <AssistantActions
          assistant={row}
          layout="compact"
          onEdit={setEditing}
          onRecoverPassword={setPendingRecover}
          onResetTwoFactor={setPendingResetTwoFactor}
          onToggleStatus={handleToggleStatus}
          onDelete={setPendingDelete}
        />
      ),
    },
  ];

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 p-4 xl:p-6'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {!embedded && (
            <>
              <h1 className="text-2xl font-bold text-gray-950 dark:text-white">{t('assistants.title')}</h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('assistants.description')}</p>
            </>
          )}
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          {createButton}
          {canManageRoles && (
            <button
              type="button"
              onClick={goToRoles}
              className={`hidden sm:inline-flex ${actionClass('outline', 'page')}`}
            >
              {t('assistants.role.manageRoles')}
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
          {actionError}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-md bg-teal-50 px-3 py-2 text-sm text-teal-800 dark:bg-teal-900/30 dark:text-teal-200">
          {notice}
        </p>
      )}

      {wizardOpen && token && (
        <CreateAssistantWizard
          token={token}
          ownerClinics={ownerClinics}
          roles={roles}
          nameFor={nameFor}
          onClose={() => setWizardOpen(false)}
          onCreated={async (info) => {
            await refresh();
            if (info?.roleAssignFailed) {
              setActionError(t('assistants.wizard.roleAssignFailed'));
            }
          }}
        />
      )}

      {editing && (
        <EditAssistantModal
          key={editing.id}
          assistant={editing}
          ownerClinics={ownerClinics}
          roles={roles}
          nameFor={nameFor}
          onRename={handleRename}
          onSaveClinics={handleSaveClinics}
          onAssignRole={handleAssignRole}
          onClose={() => setEditing(null)}
        />
      )}

      {loadError ? (
        <p className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
          {loadError}
        </p>
      ) : (
        <ResponsiveDataList
          rows={assistants}
          keyField={(row) => row.id}
          columns={columns}
          loading={loading}
          ariaLabel={t('assistants.title')}
          emptySlot={
            <EmptyState
              title={t('assistants.table.emptyTitle')}
              message={t('assistants.table.empty')}
              action={
                <button
                  type="button"
                  data-testid="assistants-empty-create"
                  onClick={openWizard}
                  className="rounded-md border border-teal-600 px-3 py-1.5 text-sm font-semibold text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-900/20"
                >
                  {t('assistants.wizard.open')}
                </button>
              }
            />
          }
          renderCard={(row) => (
            <AssistantCard
              assistant={row}
              roles={roles}
              onEdit={setEditing}
              onRecoverPassword={setPendingRecover}
              onResetTwoFactor={setPendingResetTwoFactor}
              onToggleStatus={handleToggleStatus}
              onDelete={setPendingDelete}
            />
          )}
        />
      )}

      {pendingRecover && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          data-testid="assistants-recover-dialog"
        >
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 font-bold text-gray-900 dark:text-white">{t('assistants.recover.title')}</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              {t('assistants.recover.confirm', { email: pendingRecover.email, name: pendingRecover.nombre })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingRecover(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleRecoverPassword()}
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
              >
                {t('assistants.actions.recoverPassword')}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {pendingResetTwoFactor && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          data-testid="assistants-reset-2fa-dialog"
        >
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 font-bold text-gray-900 dark:text-white">{t('assistants.resetTwoFactor.title')}</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              {t('assistants.resetTwoFactor.confirm', {
                email: pendingResetTwoFactor.email,
                name: pendingResetTwoFactor.nombre,
              })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingResetTwoFactor(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleResetTwoFactor()}
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
              >
                {t('assistants.actions.resetTwoFactor')}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {pendingDelete && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          data-testid="assistants-delete-dialog"
        >
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 font-bold text-gray-900 dark:text-white">{t('assistants.delete.title')}</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              {t('assistants.delete.confirm', { name: pendingDelete.nombre })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                {t('assistants.actions.delete')}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
